<template>
    <div>
        <div class="dialog-content fci-scrollbar tab-nlp p-0" ref="TabNLP">
            <!--NLP v1-->
            <div v-if="!enableNLPConfig" class="px-12 py-12 centerY">
                <!--default NLP-->
                <div style="flex: 1" class="mr-6">
                    <div class="centerY" style="margin-bottom: 2px;">
                        <div class="label mb-0 mt-0 mr-12">{{ $t('defaultNLP') }}</div>
                        <div v-if="!includesNLP(nlpName) && isShowCurrentNLP">{{ nlpName }}</div>
                    </div>
                    <v-autocomplete
                        hide-details outlined
                        :items="nlpList"
                        item-text="text"
                        item-value="value"
                        v-model="nlpCode"
                        class="input-custom"
                        @input="isShowCurrentNLP = false"
                        :readonly="!isEdit"
                    ></v-autocomplete>
                </div>

                <!--Intent Confidence-->
                <div class="confidence">
                    <div class="label mt-0">{{ $f.tc('confidence') }}</div>
                    <input
                        v-model.number="confidence"
                        type="number"
                        min="0" max="100"
                        :class="isEdit ? 'custom-focus' : ''"
                        :readonly="!isEdit"
                        @blur="validateConfidence(true)"
                    >
                </div>
            </div>
            <!-- NLP v2 v3 -->
            <div v-else class="px-12 py-12">
                <div
                    class="label mt-0 mb-6"
                    style="font-size: 14px; font-family: 'open sans bold', serif"
                >
                    {{ $f.tc('nlpConfigs') }} {{ $f.tc('useNlpEngine', useNlpEngine) }}
                </div>
                <v-form v-model="formValid" @submit="submit" ref="form">
                    <div class="nlp-config-item"
                         v-for="(item, index) in nlpConfigs"
                         :key="index"
                    >
                        <div class="centerY" style="flex: 1">
                            <div class="mr-6" style="flex: 1">
                                <div class="label mt-0">{{ $f.tc('label') }}</div>
                                <v-text-field
                                    outlined
                                    v-model="item.label"
                                    class="input-custom"
                                    :readonly="!isEdit"
                                    :rules="[formRules.ruleRequired, ruleDuplicateLabel, ruleValidCharacterLabel]"
                                    spellcheck="false"
                                ></v-text-field>
                            </div>
                            <div class="mr-6" style="flex: 1">
                                <div class="display-flex align-center">
                                    <div class="label mb-0 mt-0 mr-8">NLP</div>
                                    <div class="other-nlp"
                                         v-if="!includesNLP(item.name) && item.isShowCurrentNLP">
                                        {{ item.name }}
                                    </div>
                                </div>
                                <v-tooltip bottom nudge-bottom="-6">
                                    <template v-slot:activator="{ on }">
                                        <v-autocomplete
                                            outlined
                                            :items="scenarioSetting.version === 2 ? nlpList : nluList"
                                            item-text="text"
                                            item-value="value"
                                            v-model="item.code"
                                            class="input-custom"
                                            @input="updateItemConfig(item)"
                                            :readonly="!isEdit"
                                            :rules="[formRules.ruleRequired]"
                                            spellcheck="false"
                                            v-on="on"
                                        ></v-autocomplete>
                                    </template>
                                    <span>{{ item.name }}</span>
                                </v-tooltip>

                            </div>
                            <div class="confidence" :class="isEdit ? 'mr-6' : ''">
                                <div class="label mt-0">{{ $f.tc('confidence') }}</div>
                                <input
                                    v-model.number="item.confidence"
                                    type="number"
                                    min="0" max="100"
                                    :class="isEdit ? 'custom-focus' : ''"
                                    :readonly="!isEdit"
                                    @blur="validateConfidence(false, item)"
                                >
                            </div>
                            <div v-if="isEdit" style="width: 28px">
                                <div class="label mt-0" style="height: 19px"></div>
                                <div style="height: 32px" class="centerXY"
                                     @click="removeNLPConfig(index)">
                                    <div class="btn-icon">
                                        <img src="/img/delete-new.svg" alt="btn-delete-nlp-config">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </v-form>
                <div class="display-flex align-center justify-start mt-8" v-if="isEdit">
                    <div class="btn-outline" @click="addNLPConfig">
                        <img src="/img/add-blue.svg" alt="icon-add"
                             style="transform: scale(1.2); margin-right: 8px">
                        {{ $f.tc('addNew') }}
                    </div>
                </div>
                <div v-if="!isEdit && nlpConfigs.length === 0" style="color: #96ADD0;">
                    {{ $f.tc('noDataAvailable') }}
                </div>
            </div>
        </div>
        <div class="dialog-footer" v-if="isEdit">
            <div class="btn-custom mr-0"
                 @click="submit">{{ $f.tc('save') }}
            </div>
        </div>
    </div>
</template>

<script>
import FormMixin from "@/modules/fptai/mixins/FormMixin.vue";
import {mapState} from "vuex";
import ScenarioService from "@/services/scenario.service";
import CampaignService from "@/services/campaign.service";

export default {
    name: "ScenarioNLPTab",
    mixins: [FormMixin],
    computed: {
        ...mapState({
            treeData: state => state.scenario.treeData,
            scenario: state => state.scenario.scenario,
            scenarioSetting: state => state.scenario.scenario_setting,
            nlpList: state => state.campaign.bots ? state.campaign.bots.map(item => ({
                text: item.name,
                value: item.code,
                source: item
            })) : [],
            tenantName: state => state.scenario.tenantName,
            nluList: state => state.campaign.nlus ? state.campaign.nlus.map(item => ({
                text: item.name,
                value: item.code,
                source: item
            })) : []
        }),
        isEdit() {
            return ScenarioService.isEditable() && ScenarioService.isModeEditing()
        },
        nodesUseNLPConfig() {
            let nodeHasNLPConfig = this.treeData.nodeList.filter(i => i.config.nlpConfig)
            return nodeHasNLPConfig.filter(i => !i.config.nlpConfig.dontUseNlp)
        },
        useNlpEngine() {
            if (this.scenarioSetting.version === 3) {
                return ['NLP Platform']
            }
            return ['Chatbot']
        }
    },
    data() {
        return {
            nlpName: '',
            nlpCode: '',
            confidence: 70,
            isShowCurrentNLP: true,
            enableNLPConfig: false,
            nlpConfigs: []
        }
    },
    mounted() {
        this.getBots()
    },
    methods: {
        includesNLP(nlpName) {
            if (this.scenarioSetting?.version === 3) {
                return this.nluList.find(i => i.source.name === nlpName)
            }
            return this.nlpList.find(i => i.source.name === nlpName)
        },
        validateConfidence(isDefault, item) {
            let confidenceItem = isDefault ? this.confidence : item.confidence
            if (!confidenceItem) {
                confidenceItem = 70
                this.$forceUpdate()
                return
            }
            let confidence = parseInt(confidenceItem)
            if (confidence < 0 || confidence > 100) {
                confidenceItem = 70
                this.$forceUpdate()
                return
            }
            confidenceItem = confidence
        },
        updateValue() {
            if (this.scenario) {
                this.confidence = this.scenario.intent_confidence
                this.nlpCode = this.scenario.nlp ? this.scenario.nlp.code : ''
                this.nlpName = this.scenario.nlp ? this.scenario.nlp.name : ''
            }
        },
        updateItemConfig(item) {
            let configUsed = this.scenarioSetting.version === 3 ? this.nluList : this.nlpList
            let nlpItem = configUsed.find(i => i.source.code === item.code)
            item.code = nlpItem.source.code
            item.name = nlpItem.source.name
            item.token = nlpItem.source.token
            item.isShowCurrentNLP = false
            this.$forceUpdate()
        },
        scrollBottom(nameRef) {
            this.$nextTick(() => {
                this.$refs[nameRef].scrollTo({
                    top: this.$refs[nameRef].scrollHeight,
                    behavior: 'smooth'
                })
            })
        },
        addNLPConfig() {
            // check fields empty
            if (!this.isValidNLPConfigs()) {
                this.$appService.toastError(this.$f.tc('pleaseEnterAllFieldsInItem', [this.$f.tc('nlpConfigs')]) + this.$t('beforeAddNew'))
                return
            }
            let configUsed = this.scenarioSetting.version === 3 ? this.nluList : this.nlpList
            this.nlpConfigs.push({
                label: '',
                code: configUsed[0] ? configUsed[0].source.code : '',
                name: configUsed[0] ? configUsed[0].source.name : '',
                token: configUsed[0] ? configUsed[0].source.token : '',
                confidence: 70
            })
            this.scrollBottom('TabNLP')
        },
        isValidNLPConfigs() {
            let isValid = true
            for (let item of this.nlpConfigs) {
                if (!item.label || !item.code) {
                    isValid = false
                    break
                }
            }
            return isValid
        },
        removeNLPConfig(index) {
            if (index > -1) {
                this.nlpConfigs.splice(index, 1)
                this.$refs.form.validate()
            }
        },
        ruleDuplicateLabel(value) {
            let check = this.nlpConfigs.filter(i => i.label === value).length <= 1
            return check || this.$f.tc('duplicated')
        },
        ruleValidCharacterLabel(value) {
            let check = (/^[a-zA-Z0-9_]*$/).test(value)
            return check || this.$f.tc('onlyUseCharacter')
        },
        submit(event) {
            if (event) {
                event.preventDefault()
            }
            if (this.scenarioSetting.version === 1) {
                this.saveDefaultNLP()
                return
            }
            if (this.formValid) {
                this.saveNLPSetting()
            } else if (this.$refs.form) {
                this.$refs.form.validate()
            }
        },
        saveNLPSetting() {
            // check valid nlp configs
            if (!this.isValidNLPConfigs()) {
                this.$appService.toastError(this.$f.tc('pleaseEnterAllFieldsInItem', [this.$f.tc('nlpConfigs')]) + this.$t('beforeAddNew'))
                return
            }
            let labelNLPUsed = this.nodesUseNLPConfig.map(i => i.config.nlpConfig.label)
            let labelsNotHas = []
            for (let label of labelNLPUsed) {
                if (!this.nlpConfigs.find(i => i.label === label || i.oldLabel === label) && !labelsNotHas.includes(label)) {
                    labelsNotHas.push(label)
                }
            }
            if (labelsNotHas.length > 0) {
                this.$appService.toastError(
                    `${this.$f.tc('pleaseAddNlpConfigs', [labelsNotHas.join(", ")])}`
                )
                return
            }
            this.saveNLPConfigs()
        },
        saveDefaultNLP() {
            let model = {
                intent_confidence: this.confidence
            }
            let nlp = this.nlpList.find(item => item.value === this.nlpCode)
            if (nlp) {
                model.nlp = nlp.source
            }
            this.$emit('updateScenario', model)
        },
        saveNLPConfigs() {
            let nlpSetting = ''
            this.updateLabelNLPNodes()
            if (this.nlpConfigs.length > 0) {
                nlpSetting = {}
                this.nlpConfigs.forEach(config => {
                    nlpSetting[config.label] = {
                        code: config.code,
                        name: config.name,
                        token: config.token,
                        confidence: config.confidence
                    }
                })
                if (this.scenarioSetting.version === 3) {
                    nlpSetting['tenant_name'] = this.tenantName
                }
            }
            ScenarioService.SettingService.update(this.$route.params.scenarioId, this.scenarioSetting.id, {
                'nlp_setting': nlpSetting
            }).then(result => {
                this.$store.dispatch(this.$actionTypes.scenario.SCENARIO_SETTING, result.data)
                this.$appService.toastSuccess(this.$f.tc('save') + ' ' + this.$t('nlpConfigs') + ' ' + this.$t('success'))
                this.$emit('close')
            })
        },
        updateLabelNLPNodes() {
            let labelNLPsChanged = this.nlpConfigs.filter(item => item.label !== item.oldLabel)
            let labelMap = {}
            labelNLPsChanged.forEach(item => {
                labelMap[item.oldLabel] = item.label
            })
            if (labelNLPsChanged.length > 0) {
                let nodes = this.nodesUseNLPConfig.filter(item => labelNLPsChanged.find(i => i.oldLabel === item.config.nlpConfig.label))
                nodes.forEach(node => {
                    let newConfig = node.config
                    newConfig.nlpConfig.label = labelMap[newConfig.nlpConfig.label]
                    this.$store.dispatch(this.$actionTypes.scenario.UPDATE_NODE, {
                        scenarioId: this.$route.params.scenarioId,
                        id: node.id,
                        model: {
                            config: newConfig
                        }
                    })
                })
            }
        },
        getNlus() {
            this.$appService.showLoading()
            CampaignService.getNlus(this.tenantName).then(res => {
                this.$store.dispatch(this.$actionTypes.campaign.SET_NLUS, res.data)
            }).finally(this.$appService.hideLoading)
        },
        getBots() {
            this.$appService.showLoading()
            CampaignService.getBots().then(res => {
                this.$store.dispatch(this.$actionTypes.campaign.SET_BOTS, res.data)
            }).finally(this.$appService.hideLoading)
        },
    },
    watch: {
        scenario: {
            immediate: true,
            handler: function () {
                this.updateValue()
            }
        },
        scenarioSetting: {
            immediate: true,
            handler: function () {
                if (!this.scenarioSetting) {
                    this.nlpConfigs = []
                    return
                }
                if (this.scenarioSetting.version === 3) {
                    this.getNlus()
                }
                this.enableNLPConfig = this.scenarioSetting.version >= 2
                // convert object nlp_setting to array
                let nlpSetting = this.scenarioSetting.nlp_setting ? this.scenarioSetting.nlp_setting : []
                if (nlpSetting.length === 0) {
                    this.nlpConfigs = []
                    return
                }
                if (this.scenarioSetting.version === 3) {
                    nlpSetting = Object.fromEntries(
                        Object.entries(nlpSetting).filter(([key]) => key !== 'tenant_name'))
                }
                let arr = []
                for (let key of Object.keys(nlpSetting)) {
                    arr.push({
                        label: key,
                        oldLabel: this.$utils.cloneObject(key),
                        code: nlpSetting[key]['code'],
                        name: nlpSetting[key]['name'],
                        token: nlpSetting[key]['token'],
                        confidence: nlpSetting[key]['confidence'],
                        isShowCurrentNLP: true
                    })
                }
                this.nlpConfigs = arr
                this.isShowCurrentNLP = true
            }
        }
    }
}
</script>

<style scoped lang="scss">
.label {
    margin-top: 12px;
    margin-bottom: 2px;
    font-family: "open sans semi", serif;
    font-size: 13px;
}

.dialog-content {
    height: calc(80vh - 150px);
    overflow-y: auto;
}

.confidence {
    width: 85px;

    input {
        padding: 0 0 0 12px;
        height: 32px;
        line-height: 32px;
        outline: none;
        border-radius: 5px;
        border: 1px solid #D9D9D9;
        font-size: 14px;
        width: 85px;

        &.custom-focus:focus {
            outline: 2px solid #0052CC6B;
            border-color: #0052CC !important;
        }
    }
}

.nlp-config-item {
    display: flex;
    align-items: stretch;
    cursor: pointer;
    border: 1px solid #96add0;
    padding: 8px 12px;

    &:not(:first-child) {
        border-top: none;
    }
}

::v-deep {
    .tab-nlp {
        .v-label {
            font-family: 'open sans', serif !important;
            font-size: 14px !important;
            color: #000;
        }

        .v-input--selection-controls {
            padding-top: 12px !important;
        }

        .v-input--selection-controls__ripple {
            height: 0;
            width: 0;
        }
    }
}
</style>
